import { useApi } from "@platform/api";
import { QueryClient, useQuery, UseQueryResult } from "@tanstack/react-query";

import { IClientInfo } from "../pages/RFPDetails/components/rfpInformationTypes";

import { RfpsBaseParams } from "./useRfpsList";

interface RfpClient {
  id: string;
  RfpId: string;
  ClientId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  employerName: string;
  employerSitusState: string;
  numberOfEmployees: number;
  hasInternationalEmployees: boolean;
}

interface Client {
  id?: string;
  name?: string;
}

export interface Rfp {
  id: string;
  RfpClients: RfpClient[];
  autoAssignmentStatus: string;
  client?: Client;
  broker: {
    analystTeamId: number;
    createdAt: string;
    defaultOfficeLocationId: number | null;
    deletedAt: string | null;
    externalId: string | null;
    id: number;
    name: string;
    sfExternalId: string;
    type: string;
    updatedAt: string;
  };
  brokerId: number;
  carrierDueDate: string;
  createdBy: string;
  creationDate: string;
  deletedAt: string | null;
  estimatedTotalHours: number | null;
  details: {
    effectiveDate: string;
    dueDate: string;
    status: string;
    clientInfo: IClientInfo;
  };
  revisedDueDate: string;
}

export interface RfpsResponse {
  results: Rfp[];
  totalResults: number;
  totalPages?: number;
  page?: number;
  limit?: number;
  sort?: string;
}

export const getRfpsQueryKey = (params: RfpsBaseParams) =>
  [
    "rfps",
    {
      page: params.page,
      limit: params.limit,
      query: params.query,
      sortKey: params.sortKey,
      statuses: params.statusFilters.slice().sort(),
    },
  ] as const;

export async function fetchRfps(
  axios: ReturnType<typeof useApi>,
  params: RfpsBaseParams,
): Promise<RfpsResponse> {
  const { page, limit, query, sortKey, statusFilters } = params;
  const encodedSearchQuery = encodeURIComponent(query.trim().toLowerCase());
  const statusParams = statusFilters
    ? `&status=${statusFilters.join(",")}`
    : "";
  const url = `/brokerportal/v1/rfps?q=${encodedSearchQuery}&page=${page + 1}&limit=${limit}${sortKey ? `&sort=${sortKey}` : ""}${statusParams}`;
  const { data } = await axios.get<RfpsResponse>(url);

  return {
    results: data?.results,
    totalResults: data?.totalResults,
    totalPages: data?.totalPages,
    page: data?.page,
    limit: data?.limit,
    sort: data?.sort,
  };
}

export function useRfps(
  params: RfpsBaseParams,
): UseQueryResult<RfpsResponse, Error> {
  const { query } = params;
  const axios = useApi();
  return useQuery<RfpsResponse, Error>({
    queryKey: getRfpsQueryKey(params),
    enabled: query.trim().length === 0 || query.trim().length >= 3,
    queryFn: () => fetchRfps(axios, params),
  });
}

// Prefetch helper for opposite tab (caller must supply QueryClient & fetch config)
export async function prefetchRfps(
  client: QueryClient,
  axios: ReturnType<typeof useApi>,
  params: RfpsBaseParams,
): Promise<void> {
  await client.prefetchQuery({
    queryKey: getRfpsQueryKey(params),
    queryFn: () => fetchRfps(axios, params),
  });
}
